import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "rtama_cookie_consent";

export default function CookieBanner() {
  const [choice, setChoice] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const [mounted, setMounted] = useState(false);
  const location = useLocation();

  // Defer rendering so SSR/first paint isn't disrupted
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 400);
    return () => clearTimeout(t);
  }, []);

  // Don't show on admin or legal routes
  const path = location.pathname;
  if (
    path.startsWith("/admin") ||
    path === "/privacy" ||
    path === "/terms" ||
    path === "/cookies"
  )
    return null;
  if (choice || !mounted) return null;

  const handle = (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch (e) {
      // ignore
    }
    setChoice(value);
  };

  return (
    <div
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 sm:px-6 sm:pb-6"
    >
      <div
        className="max-w-3xl mx-auto bg-[#0A0A0A] text-[#FAF5E8] border-2 border-[#0A0A0A] rounded-2xl p-5 sm:p-6"
        style={{ boxShadow: "6px 6px 0 #FFD500" }}
      >
        <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
          <div className="w-10 h-10 rounded-full bg-[#FFD500] text-[#0A0A0A] flex items-center justify-center border-2 border-[#FAF5E8] shrink-0">
            <Cookie className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="font-display text-2xl leading-none mb-2">
              Cookies, briefly.
            </div>
            <p className="font-mono text-[12px] leading-relaxed text-[#FAF5E8]/85">
              We use strictly necessary cookies to make the site work and
              optional analytics cookies to understand how it&apos;s used. You
              can change your mind anytime — see our{" "}
              <Link
                to="/cookies"
                className="underline underline-offset-2 decoration-2 decoration-[#FFD500] hover:text-[#FFD500]"
              >
                Cookie Policy
              </Link>{" "}
              and{" "}
              <Link
                to="/privacy"
                className="underline underline-offset-2 decoration-2 decoration-[#FFD500] hover:text-[#FFD500]"
              >
                Privacy Policy
              </Link>
              .
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                data-testid="cookie-accept"
                onClick={() => handle("accepted")}
                className="inline-flex items-center gap-2 bg-[#FFD500] text-[#0A0A0A] px-5 py-2.5 rounded-full font-mono text-[11px] uppercase tracking-[0.22em] border-2 border-[#FFD500] hover:bg-[#FAF5E8] hover:border-[#FAF5E8] transition-colors"
              >
                Accept all
              </button>
              <button
                data-testid="cookie-reject"
                onClick={() => handle("rejected")}
                className="inline-flex items-center gap-2 bg-transparent text-[#FAF5E8] px-5 py-2.5 rounded-full font-mono text-[11px] uppercase tracking-[0.22em] border-2 border-[#FAF5E8] hover:bg-[#FAF5E8] hover:text-[#0A0A0A] transition-colors"
              >
                Reject non-essential
              </button>
              <span className="font-hand text-xl text-[#FF2D9C] ml-1">
                no spam — promise
              </span>
            </div>
          </div>
          <button
            data-testid="cookie-close"
            onClick={() => handle("rejected")}
            aria-label="Close"
            className="absolute sm:static top-3 right-3 sm:top-auto sm:right-auto text-[#FAF5E8]/60 hover:text-[#FAF5E8]"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
